// Mesures correctives (toutes) : filtres par état et échéance dépassée, constats en conflit de
// synchronisation à trancher par le responsable.
import { CODES_ETATS_MESURE, ETATS_MESURE } from '@cc/contrats';
import { Bandeau, EtatVide, Filtres, Lien, LienBouton, Panneau, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, ListChecks } from 'lucide-react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useConflits, useMesures } from './api';
import { TON_MESURE } from './logique';
import type { Mesure } from './types';
import styles from '../proprete/proprete.module.css';
import commun from '../../commun/commun.module.css';

export function PageMesures() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('inspections-mesures', { etat: '', echues: '' });
  const parametres: Record<string, string> = { limite: '200' };
  if (filtres.etat) parametres.etat = filtres.etat;
  if (filtres.echues) parametres.echues = filtres.echues;
  const mesures = useMesures(parametres);
  const peutValider = peut(contexte, 'inspection', 'valider');
  const conflits = useConflits(peutValider);
  const colonnes: ColonneTableau<Mesure>[] = [
    { id: 'ordre', entete: 'Ordre de mission', enTeteLigne: true, masquable: false, cellule: (m) => <Lien href={`/inspections/constats/${m.constat_id}`} className={commun.reference}>{m.ordre.numero ?? ''}</Lien>, valeurTri: (m) => m.ordre.numero ?? '' },
    { id: 'objet', entete: 'Objet contrôlé', cellule: (m) => m.ordre.objet_libelle ?? '' },
    { id: 'description', entete: 'Mesure', cellule: (m) => m.description },
    { id: 'echeance', entete: 'Échéance', cellule: (m) => <span className={m.echue ? styles.motif : undefined}>{date(m.echeance)}{m.echue ? ', dépassée' : ''}</span>, valeurTri: (m) => m.echeance },
    { id: 'delai', entete: 'Délai', numerique: true, cellule: (m) => `${m.delai_jours} j ouvrés`, valeurTri: (m) => m.delai_jours },
    { id: 'etat', entete: 'État', cellule: (m) => <PuceEtat ton={TON_MESURE[m.etat]} libelle={m.etat_libelle} />, valeurTri: (m) => m.etat },
  ];
  return (
    <>
      <EnTetePage etiquette="Inspections et contrôles" titre="Mesures correctives" description="Mesures demandées à la suite des constats, échéance en jours ouvrés du calendrier de la collectivité ; suivi sans sanction." actions={<LienBouton href="/inspections" icone={ArrowLeft} variante="secondaire">Ordres</LienBouton>} />
      {peutValider ? (
        <Panneau titre={`Constats en conflit de synchronisation (${conflits.data?.length ?? 0})`} niveau={2}>
          {(conflits.data ?? []).length === 0 ? <p className={commun.na}>Aucun constat en conflit : aucune décision n&apos;est attendue.</p> : (
            <>
              <Bandeau type="info">Ces constats ont été reçus d&apos;un appareil alors que l&apos;ordre de mission avait changé. Retenez ou écartez chacun depuis sa fiche ; rien n&apos;est écrasé (R9).</Bandeau>
              <ul className={styles.pointsListe}>
                {(conflits.data ?? []).map((k) => (
                  <li key={k.id}><Lien href={`/inspections/constats/${k.id}`}>{k.ordre.numero ?? ''} · {k.ordre.objet_libelle ?? ''}</Lien> <span className={commun.na}>reçu le {dateHeure(k.recu_le)}, {k.conclusion_libelle.toLowerCase()}</span></li>
                ))}
              </ul>
            </>
          )}
        </Panneau>
      ) : null}
      <Filtres groupes={[
        { id: 'echues', libelle: 'Échéance', options: [{ valeur: '', libelle: 'Toutes' }, { valeur: 'oui', libelle: 'Dépassée' }, { valeur: 'non', libelle: 'À venir ou close' }] },
        { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...CODES_ETATS_MESURE.map((e) => ({ valeur: e, libelle: ETATS_MESURE[e] }))] },
      ]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={mesures.isPending} erreur={mesures.error} onReessayer={() => void mesures.refetch()} lot="L3-B" fonction="La liste des mesures correctives">
        {(mesures.data ?? []).length === 0 ? <EtatVide titre="Aucune mesure corrective" icone={ListChecks}><p>Aucune mesure ne correspond à ces filtres.</p></EtatVide> : <Tableau legende="Mesures correctives" colonnes={colonnes} lignes={mesures.data ?? []} cleLigne={(m) => m.id} cleColonnes="inspections-mesures" />}
      </EtatRequete>
    </>
  );
}
